/*
 * Unbound Resonance: free sticker cart rules.
 *
 * The $0 price is enforced by Shopify at checkout by the automatic discount
 * "Free Resonance Sticker" (1 free sticker per order, only with qualifying
 * merchandise). This script keeps the cart itself clean:
 *  - removes stickers when no qualifying physical merchandise is left
 *    (gift cards, store credit and non-shipping items never qualify)
 *  - keeps at most 1 sticker line with quantity 1 (duplicates, extra variants,
 *    quantity changes, direct add links and repeated clicks are trimmed)
 *  - adds the sticker picked in the cart picker
 *  - adds the default sticker to a logged-in customer's first physical order,
 *    and re-checks right before checkout so it can't be skipped
 * Settings come from snippets/ur-free-sticker-config.liquid.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const SOURCE = 'ur-free-sticker';
const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

let running = null;
let rerun = false;
let rerunForce = false;
let pendingAdd = null;
let lastHadSticker = null;
let userRemoved = false;

function readConfig() {
  const element = document.getElementById('ur-free-sticker-config');
  if (!element) return null;
  try {
    const config = JSON.parse(element.textContent || '{}');
    config.ids = new Set((config.variants || []).filter(Boolean).map(Number));
    return config.ids.size ? config : null;
  } catch (error) {
    return null;
  }
}

function cartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((element) => {
    if (element instanceof HTMLElement && element.dataset.sectionId) ids.add(element.dataset.sectionId);
  });
  return Array.from(ids).join(',');
}

async function postJSON(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.description || data.message || `Cart request failed (${response.status})`);
  return data;
}

function refreshCart(cart) {
  try {
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: deferred.promise })
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: cart.sections,
        items: cart.items,
        itemCount: cart.item_count,
        source: SOURCE,
        didError: false,
      },
    });
  } catch (error) {
    if (document.querySelector('cart-items-component:not([data-drawer])')) window.location.reload();
  }
}

function unlockButtons() {
  document.querySelectorAll('[data-ur-sticker-add]').forEach((button) => {
    button.disabled = false;
    button.removeAttribute('aria-busy');
  });
}

async function run({ forceDefault = false } = {}) {
  const config = readConfig();
  if (!config) return;

  const cart = await fetch(`${ROOT}cart.js`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  }).then((response) => response.json());

  const isSticker = (item) => config.ids.has(Number(item.variant_id));
  const qualifies = cart.items.some((item) => !isSticker(item) && item.requires_shipping && !item.gift_card);
  const stickers = cart.items.filter(isSticker);

  const updates = {};
  let kept = false;
  for (const line of stickers) {
    if (qualifies && !kept) {
      kept = true;
      if (line.quantity !== 1) updates[line.key] = 1;
    } else {
      updates[line.key] = 0;
    }
  }

  if (qualifies && lastHadSticker && stickers.length === 0) userRemoved = true;

  let addId = null;
  if (qualifies && !kept) {
    if (pendingAdd && config.ids.has(Number(pendingAdd))) {
      addId = Number(pendingAdd);
    } else if (config.autoAdd && config.defaultVariant && (forceDefault || !userRemoved)) {
      addId = Number(config.defaultVariant);
    }
  }
  pendingAdd = null;

  const sections = cartSectionIds();
  let latest = null;

  if (Object.keys(updates).length) {
    latest = await postJSON(`${ROOT}cart/update.js`, { updates, sections, sections_url: window.location.pathname });
  }

  if (addId) {
    try {
      await postJSON(`${ROOT}cart/add.js`, { items: [{ id: addId, quantity: 1 }] });
    } catch (error) {
      console.warn('[free sticker] could not add sticker:', error.message);
    }
    latest = await postJSON(`${ROOT}cart/update.js`, { updates: {}, sections, sections_url: window.location.pathname });
  }

  const finalItems = latest ? latest.items : cart.items;
  lastHadSticker = finalItems.some(isSticker);

  if (latest) refreshCart(latest);
  unlockButtons();
}

function enforce(options = {}) {
  if (running) {
    rerun = true;
    if (options.forceDefault) rerunForce = true;
    return running;
  }
  running = run(options)
    .catch((error) => {
      console.warn('[free sticker]', error);
      unlockButtons();
    })
    .finally(() => {
      running = null;
      if (rerun) {
        const forceDefault = rerunForce;
        rerun = false;
        rerunForce = false;
        enforce({ forceDefault });
      }
    });
  return running;
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const promise = /** @type {any} */ (event).promise;
  if (!promise) return;
  promise
    .then((result) => {
      if (result && result.detail && result.detail.source === SOURCE) return;
      enforce();
    })
    .catch(() => {});
});

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-ur-sticker-add]') : null;
  if (!(button instanceof HTMLButtonElement)) return;
  event.preventDefault();
  if (button.disabled) return;
  document.querySelectorAll('[data-ur-sticker-add]').forEach((other) => {
    other.disabled = true;
  });
  button.setAttribute('aria-busy', 'true');
  pendingAdd = button.dataset.urStickerAdd;
  userRemoved = false;
  enforce();
});

document.addEventListener(
  'submit',
  async (event) => {
    const submitter = /** @type {SubmitEvent} */ (event).submitter;
    if (!submitter || submitter.getAttribute('name') !== 'checkout') return;
    if (!readConfig()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    submitter.setAttribute('aria-busy', 'true');
    try {
      await enforce({ forceDefault: true });
      while (running) await running;
    } catch (error) {
      console.warn('[free sticker]', error);
    }
    window.location.href = `${ROOT}checkout`;
  },
  true
);

enforce();